import React, { useState, useEffect, useMemo } from "react";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import { NewPhotoStates, TokenAtom, TokenInfo } from "../Hooks/TokenAtom";
import {
  AppBar,
  MenuItem,
  Menu,
  Badge,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Outlet, Link, useLocation } from "react-router-dom";
import { fetchTodayPhotoLength, fetchUserInfo } from "../Hooks/Api";
import { jwtDecode } from "jwt-decode";

const LayOut = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

  const tokens = useRecoilValue(TokenAtom);
  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const setAccessToken = useSetRecoilState(TokenAtom);
  const setTokenInfo = useSetRecoilState(TokenInfo);
  const [todayPhotosCount, setTodayPhotosCount] =
    useRecoilState(NewPhotoStates);

  const decodedToken = useMemo(() => jwtDecode(tokens.access), [tokens.access]);
  const { user_id } = decodedToken;

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleLogout = () => {
    setAccessToken({ access: undefined, refresh: undefined });
    handleMenuClose();
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleLogout}>로그아웃</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="inherit"
        >
          <Badge badgeContent={17} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls={menuId}
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const location = useLocation();

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const data = await fetchUserInfo(user_id, tokens);
        setTokenInfo({
          id: data.data.id,
          is_superuser: data.data.is_superuser,
          first_name: data.data.first_name,
        });
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };
    getUserInfo();
    fetchTodayPhotoLength(tokens, setTodayPhotosCount);
  }, [tokens, user_id, setTokenInfo, setTodayPhotosCount]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f4f2" }}>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "#f5f4f2 !important",
          }}
        >
          <Toolbar>
            {/* 중앙 정렬 메뉴 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 6, // 메뉴 간격
                flexGrow: 1,
              }}
            >
              {/* 사진첩 */}
              <Link to="/Home" style={{ textDecoration: "none" }}>
                {todayPhotosCount.count1 ? (
                  <Badge
                    badgeContent="N"
                    color="error"
                    overlap="rectangular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.6rem",
                        height: "14px",
                        minWidth: "14px",
                        padding: "0",
                        transform: "scale(0.8) translate(20%, -20%)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        color: "#8c6744",
                        cursor: "pointer",
                        borderBottom:
                          location.pathname === "/Home" ? "2px solid" : "none",
                      }}
                    >
                      사진첩
                    </Typography>
                  </Badge>
                ) : (
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      color: "#8c6744",
                      cursor: "pointer",
                      borderBottom:
                        location.pathname === "/Home" ? "2px solid" : "none",
                    }}
                  >
                    사진첩
                  </Typography>
                )}
              </Link>

              {/* 캠코더 */}
              <Link to="/CamCorder" style={{ textDecoration: "none" }}>
                {todayPhotosCount.count3 ? (
                  <Badge
                    badgeContent="N"
                    color="error"
                    overlap="rectangular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.6rem",
                        height: "14px",
                        minWidth: "14px",
                        padding: "0",
                        transform: "scale(0.8) translate(20%, -20%)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        color: "#8c6744",
                        cursor: "pointer",
                        borderBottom:
                          location.pathname === "/CamCorder"
                            ? "2px solid"
                            : "none",
                      }}
                    >
                      캠코더
                    </Typography>
                  </Badge>
                ) : (
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      color: "#8c6744",
                      cursor: "pointer",
                      borderBottom:
                        location.pathname === "/CamCorder"
                          ? "2px solid"
                          : "none",
                    }}
                  >
                    캠코더
                  </Typography>
                )}
              </Link>

              {/* 사진일기 */}
              <Link to="/PhotoDiary" style={{ textDecoration: "none" }}>
                {todayPhotosCount.count2 ? (
                  <Badge
                    badgeContent="N"
                    color="error"
                    overlap="rectangular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.6rem",
                        height: "14px",
                        minWidth: "14px",
                        padding: "0",
                        transform: "scale(0.8) translate(20%, -20%)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        color: "#8c6744",
                        cursor: "pointer",
                        borderBottom:
                          location.pathname === "/PhotoDiary"
                            ? "2px solid"
                            : "none",
                      }}
                    >
                      사진일기
                    </Typography>
                  </Badge>
                ) : (
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1.1rem",
                      color: "#8c6744",
                      cursor: "pointer",
                      borderBottom:
                        location.pathname === "/PhotoDiary"
                          ? "2px solid"
                          : "none",
                    }}
                  >
                    사진일기
                  </Typography>
                )}
              </Link>
            </Box>

            {/* 오른쪽 프로필 */}
            <Box sx={{ display: { md: "flex" } }}>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                sx={{ color: "#8c6744" }}
              >
                <AccountCircle />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {renderMobileMenu}
        {renderMenu}
      </Box>

      <Box
        sx={{
          pt: 8,
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "#ede1d1",
        }}
      >
        <Container sx={{ backgroundColor: "transparent", px: 1 }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default LayOut;
